"use client"

import * as React from "react"
import { Progress as ProgressPrimitive } from "@base-ui/react/progress"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

const progressIndicatorVariants = cva("transition-all duration-500", {
  variants: {
    variant: {
      primary: "bg-primary",
      secondary: "bg-muted-foreground/55 dark:bg-muted-foreground/50",
      destructive: "bg-destructive",
      success: "bg-success",
      warning: "bg-warning",
      info: "bg-info",
    },
  },
  defaultVariants: {
    variant: "primary",
  },
})

type ProgressVariant = NonNullable<
  VariantProps<typeof progressIndicatorVariants>["variant"]
>

const ProgressVariantContext = React.createContext<ProgressVariant>("primary")

function Progress({
  className,
  children,
  variant = "primary",
  ...props
}: ProgressPrimitive.Root.Props & {
  variant?: ProgressVariant
}) {
  return (
    <ProgressVariantContext.Provider value={variant}>
      <ProgressPrimitive.Root
        data-slot="progress"
        className={cn("flex w-full flex-col gap-2", className)}
        {...props}
      >
        {children ? (
          children
        ) : (
          <ProgressTrack>
            <ProgressIndicator />
          </ProgressTrack>
        )}
      </ProgressPrimitive.Root>
    </ProgressVariantContext.Provider>
  )
}

function ProgressLabel({ className, ...props }: ProgressPrimitive.Label.Props) {
  return (
    <ProgressPrimitive.Label
      data-slot="progress-label"
      className={cn("text-sm font-medium", className)}
      {...props}
    />
  )
}

function ProgressTrack({ className, ...props }: ProgressPrimitive.Track.Props) {
  return (
    <ProgressPrimitive.Track
      data-slot="progress-track"
      className={cn(
        "block h-1.5 w-full overflow-hidden rounded-full bg-input",
        className
      )}
      {...props}
    />
  )
}

function ProgressIndicator({
  className,
  variant: variantProp,
  ...props
}: ProgressPrimitive.Indicator.Props & {
  variant?: ProgressVariant
}) {
  const contextVariant = React.useContext(ProgressVariantContext)
  const variant = variantProp ?? contextVariant

  return (
    <ProgressPrimitive.Indicator
      data-slot="progress-indicator"
      className={cn(progressIndicatorVariants({ variant }), className)}
      {...props}
    />
  )
}

function ProgressValue({ className, ...props }: ProgressPrimitive.Value.Props) {
  return (
    <ProgressPrimitive.Value
      data-slot="progress-value"
      className={cn("text-sm tabular-nums", className)}
      {...props}
    />
  )
}

export {
  Progress,
  ProgressLabel,
  ProgressTrack,
  ProgressIndicator,
  ProgressValue,
  progressIndicatorVariants,
}
